import { useEffect, useRef, type RefObject } from 'react'

/**
 * Ширина зоны у левого края, с которой начинается жест. Как в системных
 * жестах iOS и Android: приложение полно горизонтальных слайдеров и лент
 * (баннер, слайдер картинок в рецепте, ряды категорий), и свайп по всему
 * экрану воевал бы с ними за один и тот же жест.
 */
const EDGE_ZONE_PX = 32
/** Насколько нужно утянуть, чтобы жест сработал. */
const COMPLETE_PX = 90
/** Скорость «броска» (px/ms), при которой возвращаемся без длинного свайпа. */
const COMPLETE_VELOCITY = 0.5
/** Вертикальное движение больше этого — считаем, что человек скроллит. */
const VERTICAL_CANCEL_PX = 12
/** Горизонтальное движение, после которого жест считается начатым. */
const HORIZONTAL_START_PX = 8
/** Длительность доводки — и когда страница уезжает, и когда возвращается. */
const SETTLE_MS = 220

interface UseSwipeBackOptions {
  enabled: boolean
  onBack: () => void
  /** Контент, который едет за пальцем. Без него жест сработает без анимации. */
  targetRef?: RefObject<HTMLElement | null>
}

/** Открыт оверлей (истории, модалка) — жест навигации ему только мешает. */
const hasOpenOverlay = () => document.querySelector('[role="dialog"]') !== null

/**
 * Возврат назад свайпом от левого края экрана.
 *
 * Слушаем на этапе перехвата и на старте жеста гасим всплытие: иначе тот же
 * свайп поймает слайдер картинок в карточке рецепта и пролистает изображение
 * вместо возврата. Нативный скролл это не ломает — его блокирует только
 * preventDefault, который зовём лишь после того, как жест признан
 * горизонтальным.
 *
 * @param enabled - включён ли жест на текущем экране
 * @param onBack - что делать при завершённом жесте
 * @param targetRef - контейнер контента для анимации
 */
export const useSwipeBack = ({
  enabled,
  onBack,
  targetRef,
}: UseSwipeBackOptions) => {
  // Колбэк держим в ref: иначе слушатели переподписывались бы на каждый рендер.
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  useEffect(() => {
    if (!enabled) return

    let startX = 0
    let startY = 0
    let startedAt = 0
    let tracking = false
    let active = false
    let settleTimer = 0
    // Идёт анимация ухода: новый жест не должен её прервать, иначе таймер
    // сбросится, переход не случится, а страница останется за краем экрана.
    let leaving = false

    const el = () => targetRef?.current ?? null

    /** Сдвиг контента. Тень по левому краю даёт ощущение слоя над фоном. */
    const setShift = (dx: number, withTransition: boolean) => {
      const node = el()
      if (!node) return
      node.style.transition = withTransition
        ? `transform ${SETTLE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`
        : ''
      node.style.transform = dx > 0 ? `translate3d(${dx}px, 0, 0)` : ''
      node.style.boxShadow = dx > 0 ? '-8px 0 24px rgba(0, 0, 0, 0.18)' : ''
      node.style.willChange = dx > 0 ? 'transform' : ''
    }

    const clearShift = () => {
      const node = el()
      if (!node) return
      node.style.transition = ''
      node.style.transform = ''
      node.style.boxShadow = ''
      node.style.willChange = ''
    }

    const onTouchStart = (e: TouchEvent) => {
      if (leaving || e.touches.length !== 1 || hasOpenOverlay()) return
      const t = e.touches[0]
      if (t.clientX > EDGE_ZONE_PX) return

      window.clearTimeout(settleTimer)
      startX = t.clientX
      startY = t.clientY
      startedAt = performance.now()
      tracking = true
      active = false
      // Слайдеры реагируют именно на touchstart — не даём событию дойти до них.
      e.stopPropagation()
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking) return
      const t = e.touches[0]
      const dx = t.clientX - startX
      const dy = t.clientY - startY

      if (!active) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > VERTICAL_CANCEL_PX) {
          tracking = false
          return
        }
        if (dx < HORIZONTAL_START_PX) return
        active = true
      }

      e.stopPropagation()
      // Глушим горизонтальный оверскролл, пока ведём жест.
      if (e.cancelable) e.preventDefault()
      setShift(Math.max(0, dx), false)
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return
      const wasActive = active
      tracking = false
      active = false
      if (!wasActive) return

      const dx = (e.changedTouches[0]?.clientX ?? startX) - startX
      const velocity = dx / Math.max(1, performance.now() - startedAt)
      const done = dx > COMPLETE_PX || velocity > COMPLETE_VELOCITY

      if (!done) {
        // Не дотянул — страница возвращается на место.
        setShift(0, true)
        settleTimer = window.setTimeout(clearShift, SETTLE_MS)
        return
      }

      const node = el()
      if (!node) {
        onBackRef.current()
        return
      }
      // Доводим до края экрана и только потом меняем маршрут: если перейти
      // сразу, новый экран отрисуется уже сдвинутым и дёрнется на место.
      leaving = true
      node.style.transition = `transform ${SETTLE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`
      node.style.transform = `translate3d(${window.innerWidth}px, 0, 0)`
      settleTimer = window.setTimeout(() => {
        leaving = false
        clearShift()
        onBackRef.current()
      }, SETTLE_MS)
    }

    const opts: AddEventListenerOptions = { capture: true, passive: false }
    document.addEventListener('touchstart', onTouchStart, opts)
    document.addEventListener('touchmove', onTouchMove, opts)
    document.addEventListener('touchend', onTouchEnd, opts)
    document.addEventListener('touchcancel', onTouchEnd, opts)

    return () => {
      window.clearTimeout(settleTimer)
      clearShift()
      document.removeEventListener('touchstart', onTouchStart, opts)
      document.removeEventListener('touchmove', onTouchMove, opts)
      document.removeEventListener('touchend', onTouchEnd, opts)
      document.removeEventListener('touchcancel', onTouchEnd, opts)
    }
  }, [enabled, targetRef])
}
